import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { css } from '@emotion/react'
import cn from 'classnames'
import { propFunc } from 'util/functions'
import { isForwarding } from 'util/draft'

import ToRow from '../ToRow'
import CcRow from '../CcRow'
import BccRow from '../BccRow'
import FromRow from '../FromRow'
import SubjectRow from '../SubjectRow'
import Recipients from '../Recipients'
import { styles } from './styles'

const indentedStyle = css`
  margin-left: 40px;
`

const gapStyle = css`
  gap: 6px 0;
`

const Expanded = ({
  bccVisible,
  ccVisible,
  children, // i.e. FormSwitcherButton
  className,
  draft,
  draftId,
  draftType,
  focusField,
  forwarding,
  mailboxId,
  onActionClick,
  onMailboxClick,
  onSubjectChange,
  onSubjectEnter,
  recipients,
  recipientsTo,
  recipientsCc,
  recipientsBcc,
  recipientsOpenUpward,
  subjectVisible,
  subject,
  ticketId,
  onToUpdate,
  onCcUpdate,
  onBccUpdate,
  isEditorFocused,
  onHideActions,
}) => {
  const [isEditingContact, setIsEditingContact] = useState(false)
  const isForwardingType = isForwarding(draftType) || forwarding
  const startInEditToMode = focusField === 'to'
  const toRecipients = recipientsTo || (recipients && recipients.to)
  const ccRecipients = recipientsCc || (recipients && recipients.cc)
  const bccRecipients = recipientsBcc || (recipients && recipients.bcc)

  const handleClickRecipients = useCallback(
    e => {
      if (e) {
        e.stopPropagation()
      }
      if (!toRecipients?.length) {
        onActionClick('to')
      } else if (bccRecipients.length) {
        onActionClick('bcc')
      } else if (ccRecipients.length) {
        onActionClick('cc')
      }
      setIsEditingContact(true)
    },
    [onActionClick, toRecipients, bccRecipients, ccRecipients]
  )

  useEffect(
    () => {
      if (isEditorFocused) {
        setIsEditingContact(false)
        onHideActions()
      }
    },
    [isEditorFocused, onHideActions]
  )

  useEffect(
    () => {
      if (isForwardingType) {
        setIsEditingContact(isForwardingType)
      }
    },
    [onActionClick, isForwardingType]
  )

  // HACK (jscheel): We need the rows to re-render when startInEditToMode, forwarding or focusField changes
  const rowKeyPrefix = [
    ticketId,
    isEditingContact && 'focused',
    focusField,
    forwarding && 'forwarding',
  ]
    .filter(Boolean)
    .join('-')

  const allRecipients = toRecipients
    .concat(ccRecipients, bccRecipients)
    .filter(Boolean)

  return (
    <div className={cn('grui flex flex-col', className)} css={gapStyle}>
      {!isEditingContact && (
        <Recipients
          recipients={allRecipients}
          onClickRecipients={handleClickRecipients}
        >
          {children}
        </Recipients>
      )}
      {isEditingContact && (
        <FromRow
          draft={draft}
          mailboxId={mailboxId}
          onMailboxClick={onMailboxClick}
        >
          {children}
        </FromRow>
      )}
      <div className="grui flex flex-col" css={[gapStyle, styles.moreOptions]}>
        {isEditingContact && (
          <div css={children && indentedStyle}>
            <ToRow
              bccVisible={bccVisible}
              ccVisible={ccVisible}
              fromVisible
              isForwarding={forwarding}
              onActionClick={onActionClick}
              subjectVisible={subjectVisible}
              ticketId={ticketId}
              recipients={toRecipients}
              recipientsOpenUpward={recipientsOpenUpward}
              startInEditMode={startInEditToMode}
              key={`${rowKeyPrefix}-to`}
              onUpdate={onToUpdate}
              shouldShowActions={!bccVisible && !ccVisible && !subjectVisible}
              onFocus={propFunc(onActionClick, 'to')}
            />
          </div>
        )}
        {ccVisible &&
          isEditingContact && (
            <div css={children && indentedStyle}>
              <CcRow
                recipients={ccRecipients}
                startInEditMode={focusField === 'cc'}
                ticketId={ticketId}
                recipientsOpenUpward={recipientsOpenUpward}
                onUpdate={onCcUpdate}
                key={`${rowKeyPrefix}-cc`}
                bccVisible={bccVisible}
                fromVisible
                subjectVisible={subjectVisible}
                onActionClick={onActionClick}
                shouldShowActions={!bccVisible && !subjectVisible}
                onFocus={propFunc(onActionClick, 'cc')}
              />
            </div>
          )}

        {bccVisible &&
          isEditingContact && (
            <div css={children && indentedStyle}>
              <BccRow
                recipients={bccRecipients}
                startInEditMode={focusField === 'bcc'}
                ticketId={ticketId}
                recipientsOpenUpward={recipientsOpenUpward}
                onUpdate={onBccUpdate}
                key={`${rowKeyPrefix}-bcc`}
                ccVisible={ccVisible}
                fromVisible
                subjectVisible={subjectVisible}
                onActionClick={onActionClick}
                shouldShowActions={!subjectVisible}
                onFocus={propFunc(onActionClick, 'bcc')}
              />
            </div>
          )}

        {subjectVisible &&
          isEditingContact && (
            <div css={children && indentedStyle}>
              <SubjectRow
                draft={draft}
                draftId={draftId}
                ticketId={ticketId}
                subject={subject}
                onChange={onSubjectChange}
                onEnter={onSubjectEnter}
                startInEditMode={focusField === 'subject'}
                key={`${rowKeyPrefix}-subject`}
                ccVisible={ccVisible}
                fromVisible
                bccVisible={bccVisible}
                onActionClick={onActionClick}
                shouldShowActions
                onFocus={propFunc(onActionClick, 'subject')}
              />
            </div>
          )}
      </div>
    </div>
  )
}

Expanded.propTypes = {
  draft: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  recipientsOpenUpward: PropTypes.bool,
}

Expanded.defaultProps = {
  draft: null,
  recipientsOpenUpward: false,
}

export default styled(Expanded)`
  width: 100%;
`
